/**
 * Canonical lucide icon for each module.
 *
 * Previously module icons were stored as free-form strings on `Module.icon`
 * and rendered directly in a `<span>`. That only worked for emoji — modules
 * whose icon was a lucide component *name* (e.g. 'Shield', 'Landmark',
 * 'Wrench') rendered the literal text instead of an icon. Emoji are also
 * off-brand per the design system.
 *
 * Nav chrome (module selector, home grid, coming-soon header) renders icons
 * via `<ModuleIcon>` keyed on the module id, so there is a single source of
 * truth and every module gets a real, consistent icon.
 */
import {
  Target,
  Tag,
  FileSignature,
  Banknote,
  Headphones,
  Radar,
  Boxes,
  ClipboardList,
  Users,
  FolderKanban,
  Wrench,
  HardHat,
  Clock,
  UserSearch,
  Megaphone,
  Handshake,
  ShieldCheck,
  Landmark,
  Hammer,
  FileLock,
  Brain,
  GraduationCap,
  Bot,
  Activity,
  Home,
  Warehouse,
  type LucideIcon,
} from 'lucide-react';
import type { ModuleId } from '../contexts/ModuleContext';

export const MODULE_ICONS: Record<ModuleId, LucideIcon> = {
  P1: Target, // Sales
  P2: Tag, // Quoting
  P3: FileSignature, // Contracts
  P4: Banknote, // Finance
  P5: Headphones, // Support
  P6: Radar, // Command Center
  P7: Boxes, // Assets
  P8: ClipboardList, // Forms
  P9: Users, // HR
  P10: FolderKanban, // Projects
  P11: Wrench, // Field Service
  P12: HardHat, // Technician
  P13: Clock, // Time Tracking
  P14: UserSearch, // Recruiting
  P15: Megaphone, // Marketing
  P16: Handshake, // Huddle Hub
  P17: ShieldCheck, // Safety
  P18: Landmark, // Investor Relations
  P19: Hammer, // Workshop
  P20: FileLock, // Insurance
  AI_SYSTEMS: Brain,
  TRAINING: GraduationCap,
  RUDY: Bot,
  PULSE: Activity,
  SHOP: Warehouse, // Shop
};

/** Icon for the "Home" / platform-overview entry (no active module). */
export const HOME_ICON: LucideIcon = Home;

interface ModuleIconProps {
  /** Module to render an icon for. `null` renders the Home icon. */
  moduleId: ModuleId | null;
  className?: string;
}

/** Renders the canonical lucide icon for a module (or Home when null). */
export function ModuleIcon({ moduleId, className }: ModuleIconProps) {
  const Icon = (moduleId && MODULE_ICONS[moduleId]) || HOME_ICON;
  return <Icon className={className} />;
}
